import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSections } from '../services/api';

// Import JSON data as fallback defaults
import heroData from '../data/hero.json';
import booksData from '../data/books.json';
import testimonialData from '../data/testimonial.json';
import pricingData from '../data/pricing.json';
import contactData from '../data/contact.json';
import authorData from '../data/author.json';

const SiteDataContext = createContext(null);

/**
 * Provider for site-wide data - Fetches from Neon API
 */
export function SiteDataProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    console.log('🔄 SiteDataContext: Starting to load data...');
    try {
      setLoading(true);
      setError(null);
      
      // Fetch from API (Neon database)
      console.log('📡 Calling getSections()...');
      const apiData = await getSections();
      console.log('📦 Raw API response:', apiData);
      
      // Transform API data to component-friendly format
      const transformed = transformApiData(apiData);
      setData(transformed);
      
      console.log('✅ Data loaded from Neon:', transformed);
    } catch (err) {
      console.error('❌ Failed to load from API:', err);
      setError(err.message);
      // Fallback to JSON files
      console.log('⚠️ Using default JSON data as fallback');
      setData(getDefaultData());
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadData();
    
    // Listen for data updates (when admin saves)
    const handleDataUpdate = () => {
      console.log('🔄 Data update event received, reloading...');
      loadData();
    };
    window.addEventListener('siteDataUpdated', handleDataUpdate);
    
    return () => {
      window.removeEventListener('siteDataUpdated', handleDataUpdate);
    };
  }, [loadData]);

  const refresh = useCallback(() => {
    loadData();
  }, [loadData]);

  return (
    <SiteDataContext.Provider value={{ data, loading, error, refresh }}>
      {children}
    </SiteDataContext.Provider>
  );
}

/**
 * Transform API data to component-friendly format
 */
function transformApiData(apiData) {
  const { sections, testimonials, pricingItems, contactConfig, authorConfig, bookPricingItems, pricingConfig, serviceDeRelectureConfig } = apiData;

  // Find sections by type/id
  const heroAmbitieuses = sections?.find(s => s.id === 'hero-ambitieuses' || s.name?.toLowerCase().includes('ambitieuses'));
  const bookAmbitieuses = sections?.find(s => s.id === 'book-ambitieuses' || (s.type === 'book' && s.name?.toLowerCase().includes('ambitieuses')));
  const heroDakar = sections?.find(s => s.id === 'hero-dakar' || s.name?.toLowerCase().includes('dakar'));
  const bookDakar = sections?.find(s => s.id === 'book-dakar' || (s.type === 'book' && s.name?.toLowerCase().includes('dakar')));

  return {
    // Hero sections
    herov2New: heroAmbitieuses?.data || heroData.herov2New,
    herov2: heroDakar?.data || heroData.herov2,
    
    // Book sections - transform back to expected format
    booksNew: transformBookData(bookAmbitieuses?.data) || booksData.booksNew,
    books: transformBookData(bookDakar?.data) || booksData.books,
    
    // Testimonials
    testimonial: {
      subtitle: testimonialData.testimonial?.subtitle,
      title: testimonialData.testimonial?.title,
      testimonialItem: testimonials || testimonialData.testimonial?.testimonialItem,
    },
    
    // Pricing - legacy format
    pricing: {
      subtitle: pricingData.pricing?.subtitle,
      title: pricingData.pricing?.title,
      pricingItem: pricingItems?.map(p => ({
        id: p.id,
        title: p.title,
        subtitle: p.subtitle,
        price: p.price,
        link: p.link,
        pricingList: p.features,
        popular: p.isPopular,
      })) || pricingData.pricing?.pricingItem,
    },
    
    // Book Pricing - new format with book-specific pricing
    bookPricing: bookPricingItems || [],
    
    // Pricing Config (title, subtitle for pricing section)
    pricingConfig: pricingConfig || { title: pricingData.pricing?.title, subtitle: pricingData.pricing?.subtitle },
    
    // Contact
    contact: contactConfig || contactData.contact,
    
    // Author
    authorConfig: authorConfig || authorData.authorv3,

    // Service de relecture
    serviceDeRelectureConfig: serviceDeRelectureConfig || {
      id: 'main-servicederelecture',
      image: 'assets/images/open_book.jpg',
      title: 'Service de relecture',
      subtitle: 'Besoin d\'une relecture professionnelle ?',
      description: 'Description du service de relecture...',
    },
  };
}

/**
 * Transform book section data back to component format
 */
function transformBookData(data) {
  if (!data) return null;
  
  return {
    uptitle: data.uptitle,
    subtitle: data.subtitle,
    image1: data.images?.[0] || data.image1 || '',
    image2: data.images?.[1] || data.image2 || '',
    image3: data.images?.[2] || data.image3 || '',
    image4: data.images?.[3] || data.image4 || '',
    booksItem: {
      title: data.booksItem?.title || 'De nombreux thèmes sont abordés:',
      booksItemContent: data.themes || data.booksItem?.booksItemContent || [],
    },
  };
}

/**
 * Get default data from JSON files (fallback)
 */
function getDefaultData() {
  return {
    herov2New: heroData.herov2New,
    herov2: heroData.herov2,
    booksNew: booksData.booksNew,
    books: booksData.books,
    testimonial: testimonialData.testimonial,
    pricing: pricingData.pricing,
    bookPricing: [],
    pricingConfig: { title: pricingData.pricing?.title, subtitle: pricingData.pricing?.subtitle },
    contact: contactData.contact,
    authorConfig: authorData.authorv3,
    serviceDeRelectureConfig: {
      id: 'main-servicederelecture',
      image: 'assets/images/open_book.jpg',
      title: 'Service de relecture',
      subtitle: 'Besoin d\'une relecture professionnelle ?',
      description: 'Description du service de relecture...',
    },
  };
}

/**
 * Hook to access site data
 */
export function useSiteData() {
  const context = useContext(SiteDataContext);
  if (!context) {
    throw new Error('useSiteData must be used within a SiteDataProvider');
  }
  return context;
}

/**
 * Trigger data refresh (call this after admin saves)
 */
export function triggerDataRefresh() {
  window.dispatchEvent(new Event('siteDataUpdated'));
}

export default SiteDataContext;
